import React, { useContext } from 'react';

import { ApiContext } from '_lib/api/apiContext';
import { AppointmentListDTO, ServiceTitanScheduledAppointment, SetRequiredNonNullable } from '_lib/api/apiTypes.shim';

import { ManualErrorBoundary } from '_lib/components/ManualErrorBoundary/ManualErrorBoundary';

import {
  PreviousJobSelect,
  PreviousJobSelectDisabled,
  PreviousJobSelectProps
} from '_lib/featureComponents/PreviousJobSelect/PreviousJobSelect';

type StApiPreviousJobSelectProps = Omit<PreviousJobSelectProps, 'onSetServiceTitanAppointment'> & {
  onSetServiceTitanAppointment: ({
    previousServiceTitanAppointment,
    previousCalendarAppointment
  }: {
    previousServiceTitanAppointment: ServiceTitanScheduledAppointment;
    previousCalendarAppointment: AppointmentListDTO | undefined;
  }) => Promise<void> | void;
};

const StApiPreviousJobSelectEnabled = (
  props: SetRequiredNonNullable<StApiPreviousJobSelectProps, 'providerCustomerId'>
) => {
  const { onSetServiceTitanAppointment, ...previousJobSelectProps } = props;

  const api = useContext(ApiContext);

  const upcomingCalendarAppointmentsQuery = api.appointments.useCustomerUpcomingAppointmentsCached({
    params: {
      companyId: props.companyId,
      providerCustomerId: props.providerCustomerId
    },
    timezone: previousJobSelectProps.timezone
  });

  if (upcomingCalendarAppointmentsQuery.error) {
    return (
      <ManualErrorBoundary
        titleVariant='body1'
        errorState={{ message: 'Failed to load Customer Jobs' }}
        onRefresh={async () => {
          await upcomingCalendarAppointmentsQuery.refetch();
        }}
      />
    );
  }

  return (
    <PreviousJobSelect
      onSetServiceTitanAppointment={async (previousServiceTitanAppointment) => {
        const upcomingCalendarAppointmentsQueryData = upcomingCalendarAppointmentsQuery?.data;

        const previousCalendarAppointment =
          upcomingCalendarAppointmentsQueryData && !!upcomingCalendarAppointmentsQueryData.length
            ? upcomingCalendarAppointmentsQueryData
                .sort((a, b) => Date.parse(b.modifiedAt) - Date.parse(a.modifiedAt))
                .find((a) => a.job?.providerData?.providerAppointmentId === previousServiceTitanAppointment.providerId)
            : undefined;

        onSetServiceTitanAppointment({ previousServiceTitanAppointment, previousCalendarAppointment });
      }}
      disabled={upcomingCalendarAppointmentsQuery.isFetching}
      {...previousJobSelectProps}
    />
  );
};

export const StApiPreviousJobSelect = (props: StApiPreviousJobSelectProps) => {
  const { providerCustomerId, ...restProps } = props;

  return providerCustomerId ? (
    <StApiPreviousJobSelectEnabled providerCustomerId={providerCustomerId} {...restProps} />
  ) : (
    <PreviousJobSelectDisabled required={props.required} />
  );
};
